import React, { useState, useCallback, useEffect } from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"

import { useBreakpoint } from "../hooks/useBreakpoint"

import "./footer.scss"
import Collapse from "./collapse"
import HubSpotForm from "./hubspot-form"
import { ForwardIcon } from "./icons"
import Cookie from "./pages/cookie.js"

/**
 *  @type {React.FC<fakeLinks[number]>}
 */
const FooterLinkCollapseItem = (footerData) => {
    const [closeItem, setCloseItem] = useState(true)
    const toggleCloseItem = useCallback(() => setCloseItem(prev => !prev), [])
    return (
        <div>
            <div
                tabIndex={0}
                role="button"
                className="flex flex-row justify-between items-center py-4 text-black"
                onClick={toggleCloseItem}
                onKeyDown={undefined}>
                <h1 className="footer-map-title">{footerData && footerData.title}</h1>
                <ForwardIcon className={closeItem ? "-rotate-90" : "rotate-180"} />
            </div>
            <Collapse in={closeItem}>
                {footerData && footerData.data && footerData.data.links && footerData.data.links?.map((subLink, index) => (
                    <Link
                        className={subLink?.url ? "" : "disabled-link"}
                        key={index}
                        to={subLink?.url ? subLink.url : ""}>
                        <div key={index} className="footer-map-description">
                            {subLink.title}
                        </div>
                    </Link>
                ))}
            </Collapse>
        </div>
    )
}

/**
 *  @type {React.FC<fakeLinks[number]>}
 */
const FooterLinkItem = (footerData) => {
    return (
        <div>
            <div className="footer-map-title">{footerData && footerData.title}</div>
            {footerData && footerData.data && footerData.data.links && footerData.data.links?.map((subLink, index) => (
                <Link
                    className={subLink?.url ? "" : "disabled-link"}
                    key={index}
                    to={subLink?.url ? subLink.url : ""}>
                    <div key={index} className="footer-map-description">
                        {subLink.title}
                    </div>
                </Link>
            ))}
        </div>
    )
}

/**
 * @type {React.FC<{
 *  data: fakeLinks
 *  mobile: boolean
 * }>}
 */
const FooterLinkList = ({ footer, mobile }) => {
    const renderList = () => {
        if (mobile) {
            return footer.map((item, index) =>
                index < 3 ? (
                    <FooterLinkCollapseItem key={item} {...item} />
                ) : ('')
            )
        } else {
            return footer.map((item, index) =>
                index < 3 ? (
                    <FooterLinkItem key={item} {...item} />
                ) : (''))
        }
    }
    return <>{renderList()}</>
}

const Footer = ({ footer }) => {
    const footerImgUrl = footer.footerLinksContent[3].image.url;
    const [modal, setModal] = useState(false);
    // console.log('FooterLinkCollapseItem',footer.footerLinksContent[5].data)
    useEffect(() => {
        // getCookie method is used for fetching the cookie from browser if cookie is already present then cookie modal is not showing otherwise it's showing on the website.
        // If flag value turns to 1 that means browsing cookie is already there.
        function getCookie() {
            const cookies = document.cookie.split("; ");
            let flag = 0;
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i];
                if (cookie.indexOf("browsing_history=") === 0) {
                    flag = 1;
                    break;
                }
            }
            if (flag === 1) { return false }
            else { return true };
        }
        getCookie() ?
            setModal(true) : setModal(false);
    }, [])

    const Icon = useFooterIcon()
    const { isMobile } = useBreakpoint()
    return (
        <footer className="footer">
            <div className="footer-map lg:flex justify-around">
                <div className="md:flex basis-1/2 justify-around">
                    <FooterLinkList footer={footer.footerLinksContent} mobile={isMobile} />
                </div>
                <div className="pt-8 md:pt-0 md:text-center text-center lg:text-left">
                    <img
                        width={182}
                        src={footerImgUrl}
                        alt=""
                        className="footer-map-logo"
                    />
                    <div className="footer-map-description md:justify-center lg:justify-start">
                        {footer.footerLinksContent[3].title}
                    </div>
                    <HubSpotForm formId="6bc336a3-dbfd-4de3-916b-8c072c654fda" />
                </div>
            </div>
            <div className="footer-app-area sm:flex md:justify-between">
                <div className="footer-app-area-title">
                    {footer.footerLinksContent[4].title}
                </div>
                <div className="flex lg:justify-around">
                    <div className="px-2">
                        <a
                            href={footer.footerLinksContent[4].data.links[0].url}
                            target="_blank"
                            rel="noreferrer">
                            <img
                                src={footer.footerLinksContent[4].images[0].url}
                                alt="app-store-image"
                            />
                        </a>
                    </div>
                    <div>
                        <a
                            href={footer.footerLinksContent[4].data.links[1].url}
                            target="_blank"
                            rel="noreferrer">
                            <img
                                src={footer.footerLinksContent[4].images[1].url}
                                alt="google-play-image"
                            />
                        </a>
                    </div>
                </div>
            </div>

            <div className="footer-social flex flex-col md:flex-row justify-between item-center sm:gap-6">
                <div className="footer-social-copyright">
                    {footer.footerLinksContent[5].title}
                </div>
                <div className="flex space-x-6 items-center m-0">
                    <a
                        href={footer.footerLinksContent[5].data.links[0].url}
                        target="_blank"
                        rel="noreferrer">
                        <img
                            src={footer.footerLinksContent[5].images[0].url}
                            alt="facebook"
                            height={20}
                            width={20}
                        />
                    </a>
                    <a
                        href={footer.footerLinksContent[5].data.links[1].url}
                        target="_blank"
                        rel="noreferrer">
                        <img
                            src={footer.footerLinksContent[5].images[1].url}
                            alt="twitter"
                            height={20}
                            width={20}
                        />
                    </a>
                    <a
                        href={footer.footerLinksContent[5].data.links[2].url}
                        target="_blank"
                        rel="noreferrer">
                        <img
                            src={footer.footerLinksContent[5].images[2].url}
                            alt="linkedin"
                            height={20}
                            width={20}
                        />
                    </a>
                </div>
                <div className="flex flex-col md:flex-row gap-6 md:gap-12">
                    <Link to={footer.footerLinksContent[5].data.links[3].url}>
                        <div className="footer-social-underline-text mr-6 text-center">
                            {footer.footerLinksContent[5].data.links[3].title}
                        </div>
                    </Link>
                    <Link to={"/terms-and-conditions"}>
                        <div className="footer-social-underline-text mr-6 text-center">
                        Terms and Conditions
                        </div>
                    </Link>
                    <div className="footer-social-underline-text text-center">
                        <Link to={footer.footerLinksContent[5].data.links[4].url}>{footer.footerLinksContent[5].data.links[4].title}</Link>
                    </div>
                </div>
                <Cookie isOpen={modal} closeModal={setModal} />
            </div>
        </footer>
    )
}

/** @type {Record<string,string>} */
const iconFileNameMapping = {
    ic_facebook: "Facebook",
    ic_twitter: "Twitter",
    ic_linkedin: "LinkedIn",
    ic_wordpress: "WordPress",
}
function useFooterIcon() {
    const data = useStaticQuery(graphql`
    query IconImageData {
      allFile(
        filter: {
          ext: {eq: ".svg"}
          name: {regex: "/ic_facebook|ic_twitter|ic_linkedin|ic_wordpress/"}
          sourceInstanceName: {eq: "images"}
        }
      ) {
        edges {
          node {
            publicURL
            name
          }
        }
      }
    }
  `)
    const Icon = React.useMemo(() => {
        /**
         * @typedef {{ publicURL: string; name: string }} IconImageNode
         * @type {{ node: IconImageNode }[]}
         */
        const edges = data.allFile.edges
        return edges.reduce((prev, edge) => {
            prev[iconFileNameMapping[edge.node.name]] = edge.node
            return prev
        }, /** @type {Record<string, IconImageNode>} */({}))
    }, [])

    return Icon
}

export default Footer
